import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";
import { Link } from "react-router-dom";
import { 
  Hammer, 
  Zap, 
  Wrench, 
  Wind,
  Shield, 
  CheckCircle,
  Phone,
  Award,
  Clock,
  MapPin,
  HardHat,
  Paintbrush,
  Home,
  Mail,
  Construction,
  Truck,
  AlertCircle,
  Camera,
  Fence
} from "lucide-react";
import skilledTradesTeamImage from "@/assets/skilled-trades-team.jpg";
import carpenterImage from "@/assets/carpenter-framework.jpg";
import electricianImage from "@/assets/electrician-ontario.jpg";
import plumberImage from "@/assets/plumber-ontario.jpg";
import masonImage from "@/assets/mason-brickwork.jpg";
import hvacImage from "@/assets/hvac-ontario.jpg";
import welderImage from "@/assets/welder-fabrication.jpg";
import roofingImage from "@/assets/roofing-team.jpg";
import flooringImage from "@/assets/flooring-installation.jpg";
import drywallImage from "@/assets/drywall-finishing-professional.jpg";
import heavyEquipmentImage from "@/assets/heavy-equipment-operator.jpg";
import asphaltImage from "@/assets/ontario-asphalt-paving-crew.jpg";
import restorationImage from "@/assets/roof-repair-before-after.jpg";
import droneHeroImage from "@/assets/drone-inspection-hero.jpg";
import deckFenceHeroImage from "@/assets/deck-fence-hero.jpg";
import paintingHeroImage from "@/assets/painting-services-hero.jpg";

const SkilledTradesServices = () => {
  const faqData = {
    faqs: [
      {
        question: "Do you provide electrical services in Toronto?",
        answer: "Yes. Blue Knight Contracting provides licensed electrical contracting throughout Toronto and the GTA with certified electricians for all electrical needs."
      },
      {
        question: "Can I hire multiple trades for one project?",
        answer: "Absolutely. Our general contracting team coordinates all skilled trades to deliver seamless results with integrated project management."
      },
      {
        question: "Do you service rural Ontario towns as well?",
        answer: "Yes, we proudly support both major cities and smaller towns across Ontario with our complete skilled trades services."
      },
      {
        question: "Are all your trades licensed and insured?",
        answer: "Yes, all Blue Knight Contracting skilled trades professionals are fully licensed, insured, and certified in their respective specialties across Ontario."
      }
    ]
  };

  const serviceData = {
    serviceType: "Skilled Trades",
    serviceName: "Ontario Skilled Trades Services",
    services: [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Carpenter Services Ontario",
          "description": "Professional carpentry services including framing and finish work"
        }
      },
      {
        "@type": "Offer", 
        "itemOffered": {
          "@type": "Service",
          "name": "Electrician Services Ontario",
          "description": "Licensed electrical installations and repairs across Ontario"
        }
      }
    ]
  };

  const trades = [
    {
      title: "Carpenter Services",
      description: "From framing to fine finish carpentry, our carpenters bring detail-oriented craftsmanship to residential, commercial, and industrial projects.",
      image: carpenterImage,
      alt: "Ontario carpenter building custom wooden framework",
      icon: Hammer,
      link: "/services/carpenter"
    },
    {
      title: "Electrician Services",
      description: "Licensed electricians providing wiring, lighting, power distribution, and electrical safety inspections across Ontario.",
      image: electricianImage,
      alt: "Ontario electrician handling electrical wiring installation",
      icon: Zap,
      link: "/services/electrician"
    },
    {
      title: "Plumbing Services",
      description: "Full-service plumbing installation, repair, and maintenance for homes, businesses, and industrial facilities.",
      image: plumberImage,
      alt: "Ontario plumber installing residential plumbing system",
      icon: Wrench,
      link: "/services/plumber"
    },
    {
      title: "Masonry (Brick, Block, Stone)",
      description: "Professional masons delivering durable brick, block, and stonework for both structural and decorative projects.",
      image: masonImage,
      alt: "Ontario mason constructing brick wall for a commercial property",
      icon: HardHat,
      link: "/services/masonry"
    },
    {
      title: "HVAC Technicians",
      description: "Heating, ventilation, and air conditioning experts ensuring comfort and efficiency in every season.",
      image: hvacImage,
      alt: "Ontario HVAC contractor installing commercial air system",
      icon: Wind,
      link: "/services/hvac"
    },
    {
      title: "Welding & Fabrication",
      description: "Certified welders and fabricators providing metalwork, steel reinforcement, and custom solutions.",
      image: welderImage,
      alt: "Ontario welder fabricating steel components on-site",
      icon: Shield,
      link: "/services/welding"
    },
    {
      title: "Roofing Services",
      description: "Professional roof installation, repairs, and maintenance for residential and commercial buildings.",
      image: roofingImage,
      alt: "Ontario roofing team installing shingles on a new roof",
      icon: Home,
      link: "/services/roofing"
    },
    {
      title: "Flooring Installation",
      description: "Hardwood, laminate, vinyl, and carpet installation tailored to your design and budget.",
      image: flooringImage,
      alt: "Ontario flooring contractor installing hardwood floors in residential home",
      icon: Paintbrush,
      link: "/services/flooring"
    },
    {
      title: "Drywall Services",
      description: "Expert drywall installation, taping, mudding, and finishing for smooth, paint-ready walls and ceilings.",
      image: drywallImage,
      alt: "Ontario drywall contractor finishing interior walls for residential project",
      icon: Construction,
      link: "/services/drywall"
    },
    {
      title: "Heavy Equipment Services",
      description: "Professional heavy equipment operators for excavation, grading, demolition, and site preparation.",
      image: heavyEquipmentImage,
      alt: "Ontario heavy equipment operator on construction site with excavator",
      icon: Truck,
      link: "/services/heavy-equipment"
    },
    {
      title: "Asphalt Paving",
      description: "Commercial and residential asphalt paving, resurfacing, and repair for driveways, parking lots, and roadways.",
      image: asphaltImage,
      alt: "Ontario asphalt paving crew resurfacing commercial parking lot",
      icon: HardHat,
      link: "/services/asphalt-paving"
    },
    {
      title: "Restoration Services",
      description: "Emergency restoration services for fire, water, and storm damage with insurance claim support.",
      image: restorationImage,
      alt: "Ontario restoration team repairing roof damage from storm",
      icon: AlertCircle,
      link: "/services/restoration"
    },
    {
      title: "Drone Inspections",
      description: "Advanced aerial building inspections with 4K cameras and thermal imaging for residential and commercial properties.",
      image: droneHeroImage,
      alt: "Professional drone inspection services for buildings in Toronto, Hamilton and Niagara Region",
      icon: Camera,
      link: "/services/drone-inspections"
    },
    {
      title: "Deck & Fence Installation",
      description: "Custom deck building and professional fence installation with wood, composite, and vinyl materials.",
      image: deckFenceHeroImage,
      alt: "Professional deck and fence installation services in Toronto, Hamilton and Niagara Region",
      icon: Fence,
      link: "/services/deck-fence"
    },
    {
      title: "Painting Services",
      description: "Professional interior and exterior painting for residential and commercial properties with premium paints.",
      image: paintingHeroImage,
      alt: "Professional painting services - interior and exterior painters in Toronto, Hamilton and Niagara Region",
      icon: Paintbrush,
      link: "/services/painting"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOMeta 
        title="Skilled Trades Ontario | Licensed Tradespeople & Contractors | Niagara Region, Hamilton and Toronto GTA"
        description="Professional skilled trades services in Ontario. Licensed electricians, plumbers, HVAC technicians, carpenters & more. Serving Toronto, Hamilton, Niagara & GTA."
        keywords="skilled trades Ontario, licensed trades Toronto, electrician plumber Hamilton, HVAC carpenter Niagara, trades contractors GTA, construction trades, Niagara Region skilled trades, Hamilton trades, Toronto GTA trades"
        canonicalUrl="https://blueknightcontracting.ca/services/skilled-trades"
      />
      <SchemaMarkup type="Service" data={serviceData} />
      <SchemaMarkup type="FAQ" data={faqData} />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-primary-glow text-primary-foreground py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            Skilled Trades in Ontario – Blue Knight Contracting and Construction
          </h1>
          <div className="mb-8">
            <img 
              src={skilledTradesTeamImage} 
              alt="Ontario skilled trades team including carpenters, electricians, and plumbers by Blue Knight Contracting"
              className="w-full max-w-4xl mx-auto h-auto rounded-lg shadow-xl"
            />
          </div>
          <p className="text-xl opacity-90 max-w-4xl mx-auto mb-8">
            At Blue Knight Contracting and Construction, we provide a complete team of skilled trades professionals 
            to ensure your project is completed with the highest level of craftsmanship. From carpentry and plumbing 
            to roofing and flooring, our skilled trades division brings expertise, precision, and reliability to every build.
          </p>
          <p className="text-lg opacity-80 mb-8">
            We proudly serve clients throughout Ontario, including Toronto, Ottawa, Mississauga, Hamilton, London, 
            Windsor, Kingston, Barrie, Sudbury, and Thunder Bay.
          </p>
          <Button variant="hero" size="lg" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90">
            <Phone className="mr-2 h-5 w-5" />
            Get Free Skilled Trades Quote
          </Button>
        </div>
      </section>

      {/* Expert Skilled Trades Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Expert Skilled Trades Services Across Ontario
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our comprehensive skilled trades division covers every aspect of construction with licensed, 
              experienced professionals delivering exceptional craftsmanship.
            </p>
          </div>
        </div>
      </section>

      {/* Our Skilled Trades Services */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Our Skilled Trades Services
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              From foundation to finish, our skilled trades professionals deliver quality workmanship across all construction disciplines.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {trades.map((trade, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-primary/20 overflow-hidden">
                <div className="aspect-video relative">
                  <img 
                    src={trade.image} 
                    alt={trade.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute top-4 left-4 bg-primary/90 backdrop-blur-sm p-2 rounded-lg">
                    <trade.icon className="h-5 w-5 text-primary-foreground" />
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl group-hover:text-primary transition-colors">
                    {trade.title}
                  </CardTitle>
                  <CardDescription className="text-base">
                    {trade.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to={trade.link}>
                    <Button variant="outline" className="w-full group-hover:border-primary group-hover:text-primary transition-colors">
                      Learn More About {trade.title}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Services */}
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="text-center">
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit mx-auto">
                  <Paintbrush className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Painting Services</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Interior and exterior painting for homes, offices, and industrial facilities, with quality finishes that last.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit mx-auto">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Glazier Services</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Expert glass installation for windows, storefronts, and specialty applications.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="bg-primary/10 p-3 rounded-lg w-fit mx-auto">
                  <HardHat className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Tile Setting Services</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Tile installation for floors, walls, and backsplashes, with precise alignment and durable results.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Blue Knight Contracting for Skilled Trades */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Why Blue Knight Contracting for Skilled Trades?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">One-Stop Shop</h3>
                <p className="text-muted-foreground">
                  Complete skilled trade services for all your Ontario construction needs under one roof.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Licensed & Insured</h3>
                <p className="text-muted-foreground">
                  All trades are licensed, insured, and safety-focused professionals across Ontario.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <Hammer className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Seamless Integration</h3>
                <p className="text-muted-foreground">
                  Perfect coordination with our general contracting team for unified project management.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="bg-primary/10 p-4 rounded-full w-fit mx-auto mb-4">
                  <Award className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Proven Results</h3>
                <p className="text-muted-foreground">
                  Demonstrated excellence in residential, commercial, and industrial projects across Ontario.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Areas Across Ontario */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Service Areas Across Ontario
            </h2>
            <p className="text-lg text-muted-foreground max-w-4xl mx-auto mb-8">
              Our skilled trades teams are available throughout Ontario for all your construction needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-lg">GTA Region</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>Toronto</li>
                  <li>Mississauga</li>
                  <li>Brampton</li>
                  <li>Vaughan</li>
                  <li>Markham</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-lg">Eastern Ontario</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>Ottawa</li>
                  <li>Kingston</li>
                  <li>Cornwall</li>
                  <li>Belleville</li>
                  <li>Peterborough</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-lg">Southwestern Ontario</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>London</li>
                  <li>Windsor</li>
                  <li>Kitchener-Waterloo</li>
                  <li>Cambridge</li>
                  <li>Sarnia</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-lg">Northern Ontario</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>Sudbury</li>
                  <li>Thunder Bay</li>
                  <li>North Bay</li>
                  <li>Sault Ste. Marie</li>
                  <li>Timmins</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Common questions about our skilled trades services across Ontario.
            </p>
          </div>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            {faqData.faqs.map((faq, index) => (
              <Card key={index} className="border-2 border-primary/10">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-lg mb-3 text-primary">
                    Q: {faq.question}
                  </h3>
                  <p className="text-muted-foreground">
                    A: {faq.answer}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Get a Free Skilled Trades Quote */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Get a Free Skilled Trades Quote
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
            Looking for skilled trades contractors in Ontario? Contact Blue Knight Contracting and Construction 
            today for a free estimate on all your skilled trades needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button variant="hero" size="lg" className="text-lg px-8 py-4 bg-primary-foreground text-white hover:bg-primary-foreground/90">
              <Phone className="mr-2 h-5 w-5" />
              Call (416) 555-BLUE
            </Button>
            <Button variant="contact" size="lg" className="text-lg px-8 py-4">
              <Mail className="mr-2 h-5 w-5" />
              Request Free Estimate
            </Button>
          </div>
          <div className="text-lg opacity-80">
            <p>📍 Serving all of Ontario</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SkilledTradesServices;